/**
 * Full-screen record sheet — the tap → size → count → submit loop for one order
 * at one station. A chromeless fixed overlay (not the dense inline expander the
 * supervisor workbench uses) so the whole tablet is the task: clean size tiles,
 * a calm mono readout, the on-screen number pad, one primary Submit. "Good
 * output" is the default; a shortfall is a secondary path that swaps in tap-able
 * preset reasons (a non-technical worker won't type "machine breakdown").
 * Reuses `wip.forStyle` for per-size balances and `wip.record` for the write —
 * no new backend.
 */
import { useState } from "react";
import { X, Check, AlertTriangle } from "lucide-react";
import { Alert, Button, cn, toast } from "@flint/ui";
import type { wip as wipContract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.js";
import { stationByKey } from "../../../lib/garment-stations.js";
import { NumberPad } from "./number-pad.js";

type WipStage = wipContract.WipStage;

/** Preset shortfall reasons — tap, don't type. `reason` is capped at 80 chars in the contract. */
const LOSS_REASONS = [
  "Fabric defect",
  "Machine breakdown",
  "Measurement reject",
  "Stitching defect",
  "Stain / wash issue",
  "Shortage from previous stage",
  "Other",
];

export function RecordSheet({
  styleId,
  styleRef,
  name,
  stage,
  onClose,
}: {
  styleId: string;
  styleRef: string;
  name: string;
  stage: WipStage;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const station = stationByKey(stage);
  const q = trpc.wip.forStyle.useQuery({ styleId });

  const [size, setSize] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [mode, setMode] = useState<"out" | "loss">("out");
  const [reason, setReason] = useState<string | null>(null);

  const record = trpc.wip.record.useMutation({
    onSuccess: async () => {
      // Invalidate the whole `wip.*` sub-tree: one movement ripples into every
      // downstream stage row (self-linked received), the Grid lens, the
      // finished-goods board (when it's QC output), and per-style balances.
      // Also `styles` (status may flip pp_approved → in_production) and audit.
      await Promise.all([
        utils.wip.invalidate(),
        utils.styles.get.invalidate({ id: styleId }),
        utils.styles.list.invalidate(),
        utils.auditLog.list.invalidate(),
      ]);
      toast.success(
        `Recorded ${Number(qty).toLocaleString("en-IN")} × ${size}`,
        mode === "loss" ? { description: "Shortfall logged" } : undefined,
      );
      onClose();
    },
    onError: (e: { message: string }) => toast.error("Couldn't record", { description: e.message }),
  });

  const data = q.data;
  const orderQty = data?.orderQty ?? null;
  const stageRow = data?.stages.find((s) => s.stage === stage);
  const sizeRows =
    stageRow?.sizes ??
    (data?.sizes ?? []).map((s) => ({ size: s, received: 0, output: 0, loss: 0, balance: 0 }));
  const activeBalance = sizeRows.find((s) => s.size === size)?.balance ?? 0;

  const n = Number(qty) || 0;
  const loss = mode === "loss";
  const canSubmit = !!size && n > 0 && (!loss || !!reason) && !record.isPending;

  const submit = () => {
    if (!size || n <= 0) return;
    record.mutate({
      styleId,
      stage,
      size,
      kind: mode,
      qty: n,
      reason: loss ? reason : null,
      entryDate: new Date().toISOString().slice(0, 10),
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-bg">
      {/* Header — sticky while the sheet scrolls */}
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-line bg-paper px-4 py-3">
        {station ? (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-sky-50 text-sky-700">
            <station.icon className="h-5 w-5" />
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 truncate">
            <span className="font-mono text-base font-semibold text-ink">{styleRef}</span>
            <span className="truncate text-sm text-muted">{name}</span>
          </div>
          <div className="truncate text-xs text-muted">
            {station?.label}
            {orderQty != null ? ` · order ${orderQty.toLocaleString("en-IN")} pcs` : ""}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-muted transition hover:bg-raised hover:text-ink"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body — the whole overlay scrolls; the action sits inline below the keypad
          (not pinned to the viewport bottom, which OS chrome like the taskbar can cover). */}
      <div className="mx-auto flex w-full max-w-xl flex-col gap-6 p-4 pb-24">
        {q.isLoading ? (
          <p className="p-6 text-center text-muted">Loading sizes…</p>
        ) : q.error ? (
          <Alert intent="bad" title="Couldn't load this order" description={q.error.message} />
        ) : (
          <>
            {/* Step 1 — size */}
            <section>
              <h2 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted">Size</h2>
              <div className="grid grid-cols-4 gap-2">
                {sizeRows.map((s) => {
                  const selected = s.size === size;
                  const pending = s.balance > 0;
                  return (
                    <button
                      key={s.size}
                      type="button"
                      onClick={() => {
                        setSize(s.size);
                        setQty("");
                      }}
                      className={cn(
                        "flex flex-col items-center justify-center gap-0.5 rounded-xl border py-3 transition active:scale-[0.97]",
                        selected
                          ? "border-sky-600 bg-sky-50"
                          : "border-line bg-paper hover:border-line-2",
                      )}
                    >
                      <span className={cn("text-lg font-bold", selected ? "text-sky-700" : "text-ink")}>
                        {s.size}
                      </span>
                      <span
                        className={cn(
                          "font-mono text-xs tabular-nums",
                          pending ? "text-warn" : "text-muted",
                        )}
                      >
                        {pending ? `${s.balance.toLocaleString("en-IN")}` : "done"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Step 2 — count (revealed once a size is picked) */}
            {size ? (
              <section className="flex flex-col gap-4">
                {/* Output vs loss — an obvious, one-tap choice (defaults to Finished) */}
                <div className="grid grid-cols-2 gap-1 rounded-xl border border-line bg-raised p-1">
                  <button
                    type="button"
                    onClick={() => setMode("out")}
                    className={cn(
                      "h-11 rounded-lg text-sm font-semibold transition",
                      !loss ? "bg-paper text-ink shadow-sm" : "text-muted hover:text-ink",
                    )}
                  >
                    Finished
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMode("loss");
                      setReason(null);
                    }}
                    className={cn(
                      "flex items-center justify-center gap-1.5 h-11 rounded-lg text-sm font-semibold transition",
                      loss ? "bg-paper text-bad shadow-sm" : "text-muted hover:text-bad",
                    )}
                  >
                    <AlertTriangle className="h-4 w-4" /> Loss / reject
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.12em] text-muted">
                    {loss ? "Pieces lost" : "Pieces finished"}
                  </h2>
                  <span className="font-mono text-xs tabular-nums text-muted">
                    {size} · {activeBalance.toLocaleString("en-IN")} left
                  </span>
                </div>

                {/* Readout */}
                <div
                  className={cn(
                    "grid h-32 place-items-center rounded-2xl border bg-raised",
                    loss ? "border-bad-100" : "border-line",
                  )}
                >
                  <span
                    className={cn(
                      "font-mono text-7xl font-bold tabular-nums tracking-tight",
                      loss ? "text-bad" : "text-ink",
                    )}
                  >
                    {n.toLocaleString("en-IN")}
                  </span>
                </div>

                <NumberPad value={qty} onChange={setQty} balance={activeBalance} />

                {loss ? (
                  <div>
                    <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted">Reason</h3>
                    <div className="flex flex-wrap gap-2">
                      {LOSS_REASONS.map((r) => (
                        <button
                          key={r}
                          type="button"
                          onClick={() => setReason(r)}
                          className={cn(
                            "h-10 rounded-full border px-3.5 text-sm font-medium transition active:scale-[0.97]",
                            reason === r
                              ? "border-bad bg-bad-50 text-bad"
                              : "border-line bg-paper text-ink-2 hover:border-line-2",
                          )}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Action — inline, right below the input, with room to spare */}
                <Button
                  size="lg"
                  intent={loss ? "danger" : "primary"}
                  disabled={!canSubmit}
                  onClick={submit}
                  className="mt-1 h-16 w-full text-lg font-semibold"
                >
                  {loss ? (
                    <>
                      <AlertTriangle className="h-5 w-5" /> Log loss
                      {n > 0 ? ` · ${n.toLocaleString("en-IN")}` : ""}
                    </>
                  ) : (
                    <>
                      <Check className="h-5 w-5" /> Record{n > 0 ? ` ${n.toLocaleString("en-IN")} pieces` : ""}
                    </>
                  )}
                </Button>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
